import { useFindFaireDirectBrandTokenOrAliasForCurrentPage } from "@faire/deprecated-retailer-visitor-shared/app/_lib/faire-direct-visitor/faire-direct/useFindFaireDirectBrandTokenOrAliasForCurrentPage";
import { useRouter } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/useRouter";
import { useHeaderState } from "@faire/deprecated-retailer-visitor-shared/components/Header/useHeaderState";
import {
  SignUpElement,
  SignUpSource,
  usePopSignUp,
} from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { trackSignUpClick } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUpUtils";
import { blogPage } from "@faire/deprecated-retailer-visitor-shared/paths/index";
import { useSettingConsumerRedirects } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingConsumerRedirects";
import { useIsFaireDirectActiveInLoggedOut } from "@faire/faire-direct/lib/hooks/useIsFaireDirectActiveInLoggedOut/useIsFaireDirectActiveInLoggedOut";
import { useIsFaireDirectActiveForVisitor } from "@faire/faire-direct/lib/visitor/useIsFaireDirectBrandForVisitor";
import { SignUpType } from "@faire/marketplace-lib/utils/signUpType";
import { trackFaireDirectShoppableLandingPageNavSignUpClick } from "@faire/web-api--source/events/faireDirect/click/shoppableLandingPageNavSignUp";
import { trackSignupSignUpToShopButtonClick } from "@faire/web-api--source/events/signup/click/signUpToShopButton";
import { route as supportRoute } from "@faire/web-api--source/routes/www/support";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useLocalization } from "@faire/web/common/localization/useLocalization";
import { Path } from "@faire/web/common/Path";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback } from "react";

const SIGN_IN_QUERY_PARAM = "signIn";

const buildSignUpQueryParams = (
  signUpType: SignUpType,
  brandTokenOrAlias: { brandToken?: string }
): Record<string, string> => {
  const queryParams: Record<string, string> = {
    signUp: signUpType as unknown as string,
  };

  const brandToken = brandTokenOrAlias?.brandToken;
  if (brandToken) {
    queryParams.brand = brandToken;
  }

  return queryParams;
};

export const useNavigateAndHideMenu = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFaireDirectActiveInLoggedOutFromControl =
    useIsFaireDirectActiveInLoggedOut();

  const brandTokenOrAlias = useFindFaireDirectBrandTokenOrAliasForCurrentPage();
  const isFaireDirectActiveForVisitor = useIsFaireDirectActiveForVisitor(
    brandTokenOrAlias,
    isFaireDirectActiveInLoggedOutFromControl
  );
  const isConsumerRedirectsEnabled = useSettingConsumerRedirects();

  const { popSignUp } = usePopSignUp();
  const {
    intl: { locale },
  } = useLocalization();

  const { hideMenu } = useHeaderState();
  const navigateTo = useCallback(
    (path: string) => {
      router.push(path);
      hideMenu();
    },
    [hideMenu, router]
  );

  const navigateToHelpCenter = useCallback(() => {
    getWindowOrThrow()?.open(supportRoute, "_blank");
    hideMenu();
  }, [hideMenu]);

  const navigateToBlog = useCallback(() => {
    getWindowOrThrow()?.open(blogPage(locale), "_blank");
    hideMenu();
  }, [hideMenu, locale]);

  const navigateToSignUp = useCallback(
    (
      ev?: React.MouseEvent<unknown, MouseEvent>,
      trackingParams?: {
        element?: keyof typeof SignUpElement;
        sourceId?: string;
      },
      signUpType?: SignUpType
    ) => {
      ev?.preventDefault();

      if (isFaireDirectActiveForVisitor) {
        trackFaireDirectShoppableLandingPageNavSignUpClick();
      } else {
        trackSignupSignUpToShopButtonClick();
      }

      trackSignUpClick(
        signUpType ?? SignUpType.GENERIC,
        SignUpElement[trackingParams?.element ?? SignUpElement.CATNAV],
        trackingParams?.sourceId ?? SignUpSource.CATNAV_SIGNUP_CTA
      );

      const basePath = new Path({
        pathname,
        search: searchParams.toString(),
      });

      const queryToAppend = buildSignUpQueryParams(
        signUpType ?? SignUpType.GENERIC,
        brandTokenOrAlias as { brandToken?: string }
      );

      router.replace(basePath.append(queryToAppend).toString());

      hideMenu();
    },
    [
      hideMenu,
      isFaireDirectActiveForVisitor,
      popSignUp,
      isConsumerRedirectsEnabled,
      brandTokenOrAlias,
      pathname,
      router,
      searchParams,
    ]
  );

  const navigateToFaireMarketSignUp = useCallback(
    (trackingParams?: {
      element?: keyof typeof SignUpElement;
      sourceId?: string;
    }) => {
      popSignUp(
        SignUpType.GENERIC,
        () => null,
        {
          element:
            SignUpElement[trackingParams?.element ?? SignUpElement.CATNAV],
          sourceId:
            trackingParams?.sourceId ?? SignUpSource.FAIRE_MARKET_SIGNUP_CTA,
        },
        { faireMarket: "1" }
      );
      hideMenu();
    },
    [hideMenu, popSignUp]
  );

  const navigateToSignIn = useCallback(
    (redirectTo?: string) => {
      if (searchParams.get(SIGN_IN_QUERY_PARAM)) {
        return;
      }
      const path = new Path({ pathname, search: searchParams.toString() });
      router.replace(path.append({ signIn: "1", redirectTo }).toString());
      hideMenu();
    },
    [hideMenu, pathname, router, searchParams]
  );

  return {
    navigateToBlog,
    navigateToSignUp,
    navigateToFaireMarketSignUp,
    navigateToSignIn,
    navigateTo,
    navigateToHelpCenter,
  };
};
